import CurrencyIcon from '@/components/common/CurrencyIcon';
import { numberFormatterConfig } from '@/config/config';
import { useAppSelector } from '@/state/hooks';
import { selectNopBalance } from '@/state/reducers/balanceSlice';
import useLanguage from '@/utils/hooks/useLanguage';
import BigNumber from 'bignumber.js';
import cn from 'classnames';
import { useMemo } from 'react';
import { useNumberFormatter } from 'react-aria';
import { useFormContext } from 'react-hook-form';
import { MdSwapHoriz } from 'react-icons/md';

const ConvertConfirmationTab = ({ conversionProps }: any) => {
    const { pair, type, estimatedProceeds } = conversionProps;

    const { watch } = useFormContext();
    const [convertFrom, convertTo, amount] = watch(['convertFrom', 'convertTo', 'amount']);

    const lang = useLanguage();

    const numberFormatter = useNumberFormatter(numberFormatterConfig);

    const bidAsk = useAppSelector((state) => {
        return state.marketPair.bidAsk[pair] || {};
    });
    const nopBalance = useAppSelector(selectNopBalance);

    const balances = useMemo(() => nopBalance?.balances || [], [nopBalance]);

    const convertFromBalance = useMemo(
        () => balances.find((b) => b.currency === convertFrom.value)?.totalAmount || 0,
        [balances, convertFrom.value]
    );
    const convertToBalance = useMemo(
        () => balances.find((b) => b.currency === convertTo.value)?.totalAmount || 0,
        [balances, convertTo.value]
    );

    const [convertFromCcy, convertToCcy] = useMemo(() => pair.split('/'), [pair]);

    const balancesAfterConversion = useMemo(
        () => ({
            from: BigNumber(convertFromBalance).minus(amount).toNumber(),
            to: BigNumber(convertToBalance).plus(estimatedProceeds).toNumber()
        }),
        [estimatedProceeds]
    );

    return (
        <div className="py-2 h-auto space-y-4">
            <div>
                <span className="text-brand-neutral-400 text-xs">{lang.common.from}</span>
                <div className="flex items-center justify-between w-full rounded-md bg-brand-background-dark border border-brand-neutral-700 py-2 px-3 text-left text-sm">
                    <div className="flex items-center gap-2">
                        <CurrencyIcon ccy={convertFrom?.ccy || ''} />
                        <div className="flex flex-col">
                            <span className="block truncate text-sm">{convertFrom?.label || 'N/A'}</span>
                            <span className="text-brand-neutral-400 text-2xs sm:text-xs leading-normal">
                                {convertFrom?.value || 'N/A'}
                            </span>
                        </div>
                    </div>
                    <div className="flex flex-col items-end justify-end">
                        <span className="font-bold text-brand-text">{numberFormatter.format(amount)}</span>
                        <span className="text-2xs text-brand-neutral-400 leading-none">
                            {lang.lite.youHave}{' '}
                            <u>
                                {convertFromBalance} {convertFrom.value}
                            </u>
                        </span>
                    </div>
                </div>
            </div>
            <div>
                <span className="text-brand-neutral-400 text-xs">{lang.common.to}</span>
                <div className="flex items-center justify-between w-full rounded-md bg-brand-background-dark border border-brand-neutral-700 py-2 px-3 text-left text-sm">
                    <div className="flex items-center gap-2">
                        <CurrencyIcon ccy={convertTo?.ccy || ''} />
                        <div className="flex flex-col">
                            <span className="block truncate text-sm">{convertTo?.label || 'N/A'}</span>
                            <span className="text-brand-neutral-400 text-2xs sm:text-xs leading-normal">
                                {convertFrom?.value || 'N/A'}
                            </span>
                        </div>
                    </div>
                    <div className="flex flex-col items-end justify-end">
                        <span className="font-bold text-brand-text">
                            {estimatedProceeds ? numberFormatter.format(estimatedProceeds) : 0}
                        </span>
                        <span className="text-2xs text-brand-neutral-400 leading-none">
                            {lang.lite.estimatedProceed}
                        </span>
                    </div>
                </div>
            </div>
            <div>
                <span className="text-brand-neutral-400 text-xs">{lang.lite.conversionRate}</span>
                <div className="flex w-full items-center justify-between rounded-md bg-brand-background-dark border border-brand-neutral-700 py-2 px-3">
                    <span className="text-brand-neutral-400 text-sm tracking-wider">
                        1 {convertFromCcy} = {numberFormatter.format(bidAsk[type])} {convertToCcy}
                    </span>
                    <span className="flex justify-center gap-2">
                        <CurrencyIcon ccy={convertFrom.ccy} />
                        <MdSwapHoriz className="h-6 w-6" />
                        <CurrencyIcon ccy={convertTo.ccy} />
                    </span>
                </div>
            </div>
            <div>
                <span className="text-brand-neutral-400 text-xs">{lang.lite.estimatedBalancesAfterConversion}</span>
                <div className="flex flex-col w-full items-center justify-between rounded-md bg-brand-background-dark border border-brand-neutral-700 py-2 px-3 gap-2">
                    <Balance convertTo={convertFrom} balance={balancesAfterConversion.from} />
                    <Balance convertTo={convertTo} balance={balancesAfterConversion.to} />
                </div>
            </div>
        </div>
    );
};

export default ConvertConfirmationTab;

const Balance = ({ convertTo, balance }: any) => {
    const numberFormatter = useNumberFormatter(numberFormatterConfig);
    const balanceColor = useMemo(() => {
        if (balance === 0) return 'text-brand-text';
        else if (balance > 0) return 'text-brand-green';
        else return 'text-brand-red';
    }, [balance]);

    return (
        <div className="flex w-full gap-3 text-xs items-center sm:text-sm">
            <div className="flex flex-[2_1_0] items-center gap-2">
                <CurrencyIcon ccy={convertTo?.ccy || ''} />
                <div className="flex flex-col">
                    <span className="block truncate text-sm">{convertTo?.label || 'N/A'}</span>
                    <span className="text-brand-neutral-400 text-2xs sm:text-xs leading-normal">
                        {convertTo?.value || 'N/A'}
                    </span>
                </div>
            </div>
            <div className={cn('flex flex-[1_1_0] flex-col text-end font-bold', { [balanceColor]: true })}>
                {numberFormatter.format(balance)}
            </div>
        </div>
    );
};
